/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/ban-ts-comment */
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, useMap, useMapEvent } from 'react-leaflet';
import classNames from 'classnames';
import 'mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl-leaflet';
import 'leaflet-draw';
import L, { divIcon, DrawMap, Layer } from 'leaflet';
import { v7 as uuidv7 } from 'uuid';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { SearchBox as SearchBoxComponent } from '@mapbox/search-js-react';
import NiceModal from '@ebay/nice-modal-react';
import BuildingDetailsModal from '../../modals/BuildingDetailsModal';
import InputLabel from '../../inputs/InputLabel';
import { useFormContext } from 'react-hook-form';
import * as turf from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import { Field } from '../../../types';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const SearchBox = SearchBoxComponent;

window.type = true;

L.drawLocal.draw.toolbar.buttons.polygon = 'DRAW POLYGON TEXT';

L.DrawToolbar.include({
  getModeHandlers: function (map: DrawMap) {
    return [
      {
        enabled: this.options.polygon,
        handler: new L.Draw.Polygon(map, { ...this.options.polygon }),
        title: L.drawLocal.draw.toolbar.buttons.polygon,
      },
    ];
  },
});

let mapboxLayer: any;

const DrawControl: FC<{
  mapInstanceRef: React.MutableRefObject<L.Map | undefined>;
  drawnItemsRef: React.MutableRefObject<L.FeatureGroup>;
  initialValue: Record<string, any>;
}> = memo(({ mapInstanceRef, drawnItemsRef, initialValue }) => {
  const map = useMap(); // Use React-Leaflet's hook to get the map instance
  const form = useFormContext();

  useEffect(() => {
    map.zoomControl.remove();
    const mapboxStreet = L.mapboxGL({
      accessToken: import.meta.env.VITE_ACCESS_TOKEN,
      style: 'mapbox://styles/thomas-kec/cmb9nl7yp00um01s67ewzdso2',
    });

    const mapboxSatellite = L.mapboxGL({
      accessToken: import.meta.env.VITE_ACCESS_TOKEN,
      style: 'mapbox://styles/thomas-kec/cmb9nl7yp00um01s67ewzdso2',
    }).addTo(map);

    mapboxLayer = mapboxSatellite;

    const baseMaps = {
      'Street View': mapboxStreet,
      'Satellite View': mapboxSatellite,
    };

    L.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(map);

    L.control.layers(baseMaps).addTo(map);
  }, []);

  mapInstanceRef.current = map;

  return null; // No visual elements to render
});

const LocationPicker = () => {
  const mapInstanceRef = useRef<L.Map | undefined>();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  const searchBoxRef = useRef();
  const form = useFormContext();
  const locationInformation = useMemo(() => form.getValues('location_information'), []);
  const [inputValue, setInputValue] = useState(() => locationInformation?.address ?? '');

  const placeMarker = (coordinates: { [key: number]: number }, address: string) => {
    const map = mapInstanceRef.current;

    if (!map) return;
    map.setView(
      {
        lat: coordinates[1],
        lng: coordinates[0],
      },
      20,
    );

    const latLng = L.latLng(coordinates[1], coordinates[0]);

    const defaultIcon = L.icon({
      iconUrl: markerIcon,
      shadowUrl: markerShadow,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    // Create and add new marker
    const marker = L.marker(latLng, { draggable: false, icon: defaultIcon }).bindPopup(address || 'Selected Location');

    marker.addTo(map);
    marker.openPopup();
    drawnItemsRef.current.addLayer(marker);
  };

  useEffect(() => {
    if (locationInformation) {
      setTimeout(() => {
        placeMarker([locationInformation.longitude, locationInformation.latitude], locationInformation.address);
      }, 0);
    }
  }, [locationInformation]);

  return (
    <>
      {/* GRID STYLES ARE HARDCODED */}
      <InputLabel
        q={{ label: 'Geben Sie hier die Adresse Ihres Standortes ein.' } as Field}
        className="col-start-1 row-start-1"
      >
        {/*// @ts-ignore*/}
        <SearchBox
          mapboxgl={mapboxgl}
          ref={searchBoxRef}
          accessToken={import.meta.env.VITE_ACCESS_TOKEN}
          value={inputValue}
          onChange={d => {
            setInputValue(d);
          }}
          onRetrieve={value => {
            const buttonElement = document.activeElement as HTMLElement;
            buttonElement.blur();

            drawnItemsRef.current.clearLayers();

            const feature = value.features[0];
            if (!feature) return;

            const coordinates = feature.geometry?.coordinates;
            const postcode = feature.properties?.context?.postcode?.name;

            if (postcode) {
              form.setValue('meta_information.postcode_asset', postcode, { shouldDirty: true, shouldTouch: true });
            }

            if (coordinates && mapInstanceRef.current) {
              placeMarker(coordinates, feature.properties?.address);

              form.setValue('location_information', {
                // @ts-ignore
                street_name: feature.properties?.context?.address?.street_name || '',
                // @ts-ignore
                street_number: feature.properties?.context?.address?.address_number || '',
                postcode: postcode || '',
                city: feature.properties?.context?.place?.name || feature.properties?.context?.locality?.name || '',
                mapbox_id: feature.properties?.mapbox_id ?? null,
                latitude: +parseFloat((feature.properties?.coordinates?.latitude ?? 0) as unknown as string).toFixed(6),
                longitude: +parseFloat((feature.properties?.coordinates?.longitude ?? 0) as unknown as string).toFixed(
                  6,
                ),
                address: feature.properties?.address,
              });
            }
          }}
        />
      </InputLabel>

      {/* GRID STYLES ARE HARDCODED */}
      <div className="col-span-full">
        <MapContainer center={[52.520008, 13.404954]} zoom={18} className="h-96">
          <DrawControl
            drawnItemsRef={drawnItemsRef}
            mapInstanceRef={mapInstanceRef}
            initialValue={locationInformation}
          />
        </MapContainer>
      </div>
    </>
  );
};

export default LocationPicker;
